import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { PhotoName } from "@/components/ui/photo";

const Ctx = createContext<(name: PhotoName | null) => void>(() => {});

/** A screen names its picture; the frame lays it faintly behind the whole page. */
export function useBackdrop(name: PhotoName | undefined) {
  const set = useContext(Ctx);
  useEffect(() => {
    set(name ?? null);
    return () => set(null);
  }, [name, set]);
}

/**
 * The quiet operational ground behind every signed-in screen. Individual pages
 * can still show their own photo as a small contextual thumbnail in the header,
 * but the workspace itself stays dense and readable.
 */
export function BackdropProvider({ children }: { children: ReactNode }) {
  const [, setName] = useState<PhotoName | null>(null);
  return (
    <Ctx.Provider value={setName}>
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 bg-ground print:hidden" />
      {children}
    </Ctx.Provider>
  );
}
